//done
//@ts-check
import * as config from "../constants.js";
import { gameState } from "./gameState-core.js";
import { debug, isDebug, CH } from './debugLog.js';

// Multiplayer system using Cloudflare Pages Functions (functions/api/*) + Neon PostgreSQL
// Falls back to local storage when offline or headless

// How long a marketplace listing response stays reusable. Long enough to collapse
// the burst of refreshes fired during page load, short enough that another
// player's listing shows up promptly. Mutations clear the cache outright.
const MARKETPLACE_CACHE_MS = 5000;

// Sticky opt-in for talking to a locally-served API (see the constructor).
// `?multiplayer=1` turns it on and persists; `?multiplayer=0` turns it back off.
// localStorage access can throw (private mode), so treat any failure as "off".
function readMultiplayerOverride() {
    try {
        const param = new URLSearchParams(window.location.search).get('multiplayer');
        if (param === '1') localStorage.setItem('forceMultiplayer', '1');
        else if (param === '0') localStorage.removeItem('forceMultiplayer');
        return localStorage.getItem('forceMultiplayer') === '1';
    } catch (error) {
        return false;
    }
}

class MultiplayerSystem {
    constructor() {
        // Headless environments (Node simulator/tests) have no window; run local/offline
        const hasWindow = typeof window !== 'undefined';

        // Check if we're in local development (no actual server)
        this.isLocalDevelopment = !hasWindow ||
                                  window.location.hostname === 'localhost' ||
                                  window.location.hostname === '127.0.0.1' ||
                                  window.location.protocol === 'file:';

        // Local dev forces offline because `npm run dev` (Parcel) serves no API.
        // `npm run dev:api` DOES serve the real Pages Functions locally, so opt in
        // with ?multiplayer=1 to exercise the marketplace end-to-end against real
        // server logic (see README "Testing marketplace behavior locally").
        // Gated on hasWindow so the headless simulator/tests always stay offline.
        this.forceMultiplayer = hasWindow && readMultiplayerOverride();

        // Override online status for local development
        this.isOnline = hasWindow && navigator.onLine && (!this.isLocalDevelopment || this.forceMultiplayer);
        this.baseUrl = hasWindow ? window.location.origin : '';
        this.localCharacters = this.loadLocalCharacters();
        this._marketplaceCache = new Map(); // key -> { value, at } | { promise }

        if (hasWindow) {
            const mode = this.forceMultiplayer ? 'Local Development (multiplayer forced ON)'
                : this.isLocalDevelopment ? 'Local Development' : 'Production';
            debug(CH.ui, `🌐 Multiplayer mode: ${mode}`);
            debug(CH.ui, `📶 Online status: ${this.isOnline}`);
            if (this.forceMultiplayer) {
                // Easy mistake: the flag is sticky, so hitting the Parcel port with
                // it on makes every API call parse index.html as JSON ("Unexpected
                // token '<'") and every POST fail with a bare 405. Say which origin
                // we're actually calling, then confirm the API is really there.
                console.log(`🔧 API base: ${this.baseUrl} — this must be the port serving /api (npm run dev:api, default 8788), NOT the Parcel port. Use ?multiplayer=0 to turn this off.`);
                this.verifyLocalApi();
            }

            // Listen for online/offline events
            window.addEventListener('online', () => {
                this.isOnline = navigator.onLine && (!this.isLocalDevelopment || this.forceMultiplayer);
                debug(CH.ui, `Multiplayer: Network online, effective status: ${this.isOnline}`);
            });

            window.addEventListener('offline', () => {
                this.isOnline = false;
                debug(CH.ui, 'Multiplayer: Gone offline, using local storage');
            });
        }
    }

    // Confirm the forced-on API base is actually serving the Pages Functions.
    // Pointing ?multiplayer=1 at the Parcel port is the easy mistake, and it fails
    // confusingly: GETs return index.html ("Unexpected token '<'") and POSTs come
    // back as a bare 405 from the static server. Detect that once, up front, and
    // fail closed so the game drops back to offline instead of spraying dozens of
    // misleading errors. Fire-and-forget from the constructor.
    async verifyLocalApi() {
        try {
            // my-listings with no ids short-circuits server-side before touching
            // the database, so this proves "a Pages Function answered with JSON"
            // at zero cost — and doesn't masquerade as a real marketplace fetch
            // in the network tab.
            const response = await fetch(`${this.baseUrl}/api/characters?action=my-listings&ids=`);
            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('application/json')) return; // API is live

            this.isOnline = false;
            console.error(
                `❌ Multiplayer forced ON, but ${this.baseUrl} is not serving the API ` +
                `(got HTTP ${response.status}, content-type "${contentType || 'unknown'}"). ` +
                `Multiplayer disabled for this session to avoid misleading errors.\n` +
                `Fix: run "npm run dev:api" and open http://localhost:8788/?multiplayer=1 ` +
                `— the Parcel port (3000) serves no API.`
            );
        } catch (error) {
            this.isOnline = false;
            console.error(
                `❌ Multiplayer forced ON, but ${this.baseUrl}/api is unreachable (${error.message}). ` +
                `Start it with "npm run dev:api". Multiplayer disabled for this session.`
            );
        }
    }

    // Load characters from local storage (fallback)
    loadLocalCharacters() {
        if (typeof localStorage === 'undefined') return [];
        try {
            return JSON.parse(localStorage.getItem('multiplayerCharacters') || '[]');
        } catch (error) {
            console.error('Error loading local characters:', error);
            return [];
        }
    }

    // Save characters to local storage (fallback)
    saveLocalCharacters() {
        if (typeof localStorage === 'undefined') return;
        try {
            localStorage.setItem('multiplayerCharacters', JSON.stringify(this.localCharacters));
        } catch (error) {
            console.error('Error saving local characters:', error);
        }
    }

    // Check if character name is unique globally
    async isCharacterNameUnique(name) {
        debug(CH.ui, `🔍 Checking uniqueness for: "${name}"`);
        debug(CH.ui, `📶 Online status: ${this.isOnline}`);

        if (!this.isOnline) {
            // Offline fallback - check local storage for global uniqueness
            debug(CH.ui, `📊 Checking against ${this.localCharacters.length} local characters:`, this.localCharacters);

            const conflicts = this.localCharacters.filter(char =>
                char.name.toLowerCase() === name.toLowerCase()
            );

            debug(CH.ui, `⚔️ Name conflicts found:`, conflicts);

            const isUnique = conflicts.length === 0;
            debug(CH.ui, `✅ Name "${name}" is unique: ${isUnique}`);

            return isUnique;
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/characters?action=check-name&name=${encodeURIComponent(name)}`);
            const data = await response.json();

            if (data.success) {
                return data.isUnique;
            } else {
                console.error('Name check failed:', data.message);
                return false;
            }
        } catch (error) {
            console.error('Error checking name uniqueness:', error);
            // Fallback to local check for global uniqueness
            return !this.localCharacters.some(char =>
                char.name.toLowerCase() === name.toLowerCase()
            );
        }
    }

    // Register a new character
    async registerCharacter(character) {
        if (!this.isOnline) {
            throw new Error('Cannot register character while offline');
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/characters?action=create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    // Identity/cosmetic only — the server sets all stats (hardened create).
                    name: character.name,
                    faction: character.faction,
                    avatar: character.avatar,
                    avatarSeed: character.avatarSeed || null,
                    avatarFeatures: character.avatarFeatures || null,
                    title: character.title || null,
                    quote: character.quote || null
                })
            });

            const data = await response.json();

            if (data.success) {
                // Attach the one-time ownership token to the returned row so the
                // caller can persist it on the character (see gameState §2).
                return { ...data.character, ownerToken: data.token };
            } else {
                throw new Error(data.message);
            }
        } catch (error) {
            console.error('Error registering character:', error);
            throw error;
        }
    }

    // Claim a pre-token (legacy) character row, upgrading it from open "grace"
    // writes to token-owned. Only succeeds for eligible rows (server-checked:
    // null hash, level <= 2, no artifacts/gear). Returns the new token or null.
    async claimLegacyCharacter(characterId) {
        if (!this.isOnline || !characterId) return null;
        try {
            const response = await fetch(`${this.baseUrl}/api/characters?action=claim-legacy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ characterId })
            });
            const data = await response.json();
            return data.success ? data.token : null;
        } catch (error) {
            console.error('Error claiming legacy character:', error);
            return null;
        }
    }

    // Build request headers, attaching the ownership token for the character
    // with this multiplayerId when we hold it (looked up from gameState). The
    // server requires it for writes to a token-bearing row.
    _authHeaders(characterId) {
        const headers = { 'Content-Type': 'application/json' };
        const owner = (gameState.characters || []).find(c => c.multiplayerId === characterId);
        if (owner?.ownerToken) headers['Authorization'] = `Bearer ${owner.ownerToken}`;
        return headers;
    }

    // Update character data
    async updateCharacter(characterId, updates) {
        // Update local character if it exists
        const localIndex = this.localCharacters.findIndex(char => char.id === characterId);
        if (localIndex !== -1) {
            this.localCharacters[localIndex] = { ...this.localCharacters[localIndex], ...updates };
            this.saveLocalCharacters();
        }

        if (!this.isOnline) {
            return; // Only local update needed
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/characters`, {
                method: 'PUT',
                headers: this._authHeaders(characterId),
                body: JSON.stringify({
                    id: characterId,
                    ...updates
                })
            });

            const data = await response.json();

            if (!data.success) {
                console.error('Error updating character:', data.message);
            }
        } catch (error) {
            console.error('Error updating character:', error);
        }
    }

    // Get all available contractors
    async getAllContractors(excludeCharacterId = null) {
        let contractors = [];

        if (this.isOnline) {
            try {
                const url = excludeCharacterId
                    ? `${this.baseUrl}/api/characters?action=contractors&exclude=${excludeCharacterId}`
                    : `${this.baseUrl}/api/characters?action=contractors`;

                const response = await fetch(url);
                const data = await response.json();

                if (data.success) {
                    contractors = data.contractors.map(char => ({
                        ...char,
                        isPlayerCharacter: true,
                        isOnline: true
                    }));
                }
            } catch (error) {
                console.error('Error fetching online contractors:', error);
            }
        }

        // Add local characters (excluding current character)
        const localContractors = this.localCharacters
            .filter(char => char.id !== excludeCharacterId)
            .map(char => ({
                ...char,
                isPlayerCharacter: true,
                isLocal: true
            }));

        return [...contractors, ...localContractors];
    }

    // List character on marketplace
    async listCharacterOnMarketplace(characterId, durationHours = 24, rewardPercentage = 15) {
        debug(CH.ui, `📢 Listing character ${characterId} on marketplace for ${durationHours} hours`);
        
        if (!this.isOnline) {
            throw new Error('Cannot list characters on marketplace. Please connect to internet.');
        }

        try {
            debug(CH.ui, 'Sending request to list character on marketplace...');
            const response = await fetch(`${this.baseUrl}/api/characters?action=list-marketplace`, {
                method: 'POST',
                headers: this._authHeaders(characterId),
                body: JSON.stringify({
                    characterId: characterId,
                    durationHours: durationHours,
                    rewardPercentage: rewardPercentage
                })
            });
            
            debug(CH.ui, 'Listing character on marketplace, response:', response);

            const data = await response.json();
            
            debug(CH.ui, 'Listing character on marketplace, data:', data);
            
            if (data.success) {
                this.invalidateMarketplaceCache();
                return data.listing;
            } else {
                throw new Error(data.message);
            }
        } catch (error) {
            console.error('Error listing character on marketplace:', error);
            throw error;
        }
    }

    // Claim the un-hired contract-completion payout for an expired listing.
    // The server decides eligibility (expired, never hired, not already claimed)
    // and computes the reward; a payment then arrives via the normal pending
    // flow. Owner-authenticated. Returns the server response (may be
    // { claimed: false }) or null when offline.
    async claimContractCompletion(characterId) {
        if (!this.isOnline) return null;

        try {
            const response = await fetch(`${this.baseUrl}/api/characters?action=claim-contract-completion`, {
                method: 'POST',
                headers: this._authHeaders(characterId),
                body: JSON.stringify({ characterId })
            });
            return await response.json();
        } catch (error) {
            console.error('Error claiming contract completion:', error);
            throw error;
        }
    }

    // Remove character from marketplace
    async removeCharacterFromMarketplace(characterId) {
        if (!this.isOnline) {
            throw new Error('Cannot access marketplace while offline');
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/characters?action=marketplace&id=${characterId}`, {
                method: 'DELETE',
                headers: this._authHeaders(characterId)
            });

            const data = await response.json();
            if (data.success) this.invalidateMarketplaceCache();
            return data.success;
        } catch (error) {
            console.error('Error removing character from marketplace:', error);
            throw error;
        }
    }

    // Get marketplace listings with optional level filtering.
    //
    // Page load fans out several near-simultaneous refreshes (Alpine store init,
    // displayContractors, the gameState load hook, the periodic timer), which
    // previously meant ~6 identical requests. Concurrent callers now share one
    // in-flight request, and the result is held for MARKETPLACE_CACHE_MS so a
    // burst collapses to a single call. Any marketplace mutation clears the cache
    // (see invalidateMarketplaceCache), so the UI never shows stale listings.
    async getMarketplaceListings(minLevel = null, maxLevel = null, excludeIds = []) {
        if (!this.isOnline) {
            return [];
        }

        const key = `${minLevel}|${maxLevel}|${[...excludeIds].sort().join(',')}`;
        const entry = this._marketplaceCache.get(key);
        if (entry) {
            if (entry.promise) return entry.promise;                                  // already in flight
            if (Date.now() - entry.at < MARKETPLACE_CACHE_MS) return entry.value;     // still fresh
        }

        // `entry` (captured before we overwrite it below) carries the previous
        // value + ETag, so a 304 can be answered from it without a body.
        const promise = this._fetchMarketplaceListings(minLevel, maxLevel, excludeIds, entry)
            .then(({ value, etag }) => {
                this._marketplaceCache.set(key, { value, etag, at: Date.now() });
                return value;
            });
        this._marketplaceCache.set(key, { promise });
        return promise;
    }

    // Uncached fetch. Never throws — callers treat [] as "nothing available".
    //
    // Sends If-None-Match when we've seen this list before. The contractors UI
    // polls every 10s, and listings rarely change, so the overwhelmingly common
    // response is a bodiless 304: no JOIN server-side, no payload, no JSON parse.
    async _fetchMarketplaceListings(minLevel, maxLevel, excludeIds, previous) {
        try {
            let url = `${this.baseUrl}/api/characters?action=marketplace`;
            if (minLevel !== null) url += `&minLevel=${minLevel}`;
            if (maxLevel !== null) url += `&maxLevel=${maxLevel}`;
            if (excludeIds && excludeIds.length > 0) url += `&exclude=${excludeIds.join(',')}`;

            const options = previous?.etag ? { headers: { 'If-None-Match': previous.etag } } : {};
            const response = await fetch(url, options);

            // Not modified — keep what we already parsed.
            if (response.status === 304) {
                return { value: previous?.value ?? [], etag: previous?.etag ?? null };
            }

            const etag = response.headers.get('ETag');
            const data = await response.json();

            if (data.success) {
                return { value: data.listings, etag };
            }
            console.error('Error getting marketplace listings:', data.message);
            return { value: [], etag: null };
        } catch (error) {
            console.error('Error fetching marketplace listings:', error);
            return { value: [], etag: null };
        }
    }

    // Drop cached listings after anything that changes them (list, withdraw,
    // reserve, hire, release, settle) so the next read reflects the change.
    invalidateMarketplaceCache() {
        this._marketplaceCache.clear();
    }

    // Server truth for this player's own listings, keyed by character id. The
    // client caches marketplaceData at listing time and would otherwise never see
    // a server-side change to it (notably the dev time-shift tool).
    //
    // Returns an array on success (possibly empty = "you have no listings") and
    // null when we couldn't find out. Callers MUST distinguish the two: treating
    // a failed request as "no listings" would wrongly clear a live listing from
    // local state on any network blip.
    async getMyMarketplaceListings(characterIds = []) {
        if (!this.isOnline || characterIds.length === 0) {
            return null;
        }

        try {
            const url = `${this.baseUrl}/api/characters?action=my-listings&ids=${characterIds.join(',')}`;
            const response = await fetch(url);
            const data = await response.json();
            if (!data.success) {
                console.error('Error getting own marketplace listings:', data.message);
                return null;
            }
            return data.listings;
        } catch (error) {
            console.error('Error fetching own marketplace listings:', error);
            return null;
        }
    }

    // Get connection status
    getStatus() {
        return {
            isOnline: this.isOnline,
            localCharacters: this.localCharacters.length,
            hasInternetConnection: navigator.onLine
        };
    }

    // Sync local characters to online database when coming back online
    async syncLocalCharacters() {
        if (!this.isOnline) return;

        const localCharsToSync = this.localCharacters.filter(char => char.isLocal);

        for (const localChar of localCharsToSync) {
            try {
                // Check if name is still unique online
                const isUnique = await this.isCharacterNameUnique(localChar.name);

                if (isUnique) {
                    // Register the character online
                    const onlineChar = await this.registerCharacter(localChar);

                    // Remove from local characters and update any references
                    const localIndex = this.localCharacters.findIndex(char => char.id === localChar.id);
                    if (localIndex !== -1) {
                        this.localCharacters.splice(localIndex, 1);
                    }

                    debug(CH.ui, `Synced local character ${localChar.name} to online database`);
                } else {
                    console.warn(`Cannot sync ${localChar.name} - name already exists online`);
                }
            } catch (error) {
                console.error(`Failed to sync character ${localChar.name}:`, error);
            }
        }

        this.saveLocalCharacters();
    }

    // Delete own character (removes from database and local storage)
    async deleteOwnCharacter(characterId) {
        // Remove from local storage first
        const index = this.localCharacters.findIndex(char => char.id === characterId);
        if (index !== -1) {
            this.localCharacters.splice(index, 1);
            this.saveLocalCharacters();
            debug(CH.ui, `Removed character ${characterId} from local storage`);
        } else {
            console.warn(`Character ${characterId} not found in local storage`);
        }

        // Remove from online database if online
        if (this.isOnline) {
            try {
                const response = await fetch(`${this.baseUrl}/api/characters?action=character&id=${characterId}`, {
                    method: 'DELETE',
                    headers: this._authHeaders(characterId)
                });
                const data = await response.json();
                return data.success;
            } catch (error) {
                console.error('Error deleting character from database:', error);
                return false;
            }
        }

        return true;
    }

    // Create payment record for contractor payments
    async createPaymentRecord(paymentData) {
        if (!this.isOnline) {
            console.warn('Cannot create payment record while offline');
            return false;
        }

        try {
            const headers = { 'Content-Type': 'application/json' };
            if (paymentData.hirerToken) headers['Authorization'] = `Bearer ${paymentData.hirerToken}`;

            const response = await fetch(`${this.baseUrl}/api/payments?action=create`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    recipientCharacterId: paymentData.recipientCharacterId,
                    hirerCharacterId: paymentData.hirerCharacterId,
                    amount: paymentData.amount,
                    xp: paymentData.xp || 0,
                    source: paymentData.source || 'contractor_payment',
                    raidId: paymentData.raidId
                })
            });

            const data = await response.json();
            return data.success;
        } catch (error) {
            console.error('Error creating payment record:', error);
            return false;
        }
    }

    // Get pending payments for one character. Reads require the recipient's
    // token (server enforces ownership), so this is per-character, not a batch.
    async getPendingPayments(characterId) {
        if (!this.isOnline) {
            console.warn('Cannot get pending payments while offline');
            return [];
        }

        if (!characterId) return [];

        try {
            const response = await fetch(`${this.baseUrl}/api/payments?action=pending&characterId=${characterId}`, {
                headers: this._authHeaders(characterId)
            });
            const data = await response.json();

            if (data.success) {
                return data.payments;
            } else {
                console.error('Failed to get pending payments:', data.message);
                return [];
            }
        } catch (error) {
            console.error('Error getting pending payments:', error);
            return [];
        }
    }

    // Mark one character's payments as processed (requires the recipient's token).
    async markPaymentsProcessed(characterId, paymentIds) {
        if (!this.isOnline) {
            console.warn('Cannot mark payments processed while offline');
            return false;
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/payments`, {
                method: 'PUT',
                headers: this._authHeaders(characterId),
                body: JSON.stringify({
                    characterId: characterId,
                    paymentIds: paymentIds
                })
            });

            const data = await response.json();
            return data.success;
        } catch (error) {
            console.error('Error marking payments processed:', error);
            return false;
        }
    }

    // Reserve a contractor in the database to prevent concurrent reservations
    async reserveContractor(contractorCharacterId, reservedByCharacterId) {
        if (!this.isOnline) {
            console.warn('Cannot reserve contractor while offline');
            return { success: true }; // Allow local reservation when offline
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/characters?action=reserve-contractor`, {
                method: 'POST',
                headers: this._authHeaders(reservedByCharacterId),
                body: JSON.stringify({
                    contractorCharacterId: contractorCharacterId,
                    reservedByCharacterId: reservedByCharacterId
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error reserving contractor:', error);
            return { success: false, message: 'Network error' };
        }
    }

    // Release a contractor reservation in the database
    async releaseContractor(contractorCharacterId, releasedByCharacterId) {
        if (!this.isOnline) {
            console.warn('Cannot release contractor while offline');
            return { success: true }; // Allow local release when offline
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/characters?action=release-contractor`, {
                method: 'POST',
                headers: this._authHeaders(releasedByCharacterId),
                body: JSON.stringify({
                    contractorCharacterId: contractorCharacterId,
                    reservedByCharacterId: releasedByCharacterId
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error releasing contractor:', error);
            return { success: false, message: 'Network error' };
        }
    }

    // Hire a contractor (transition from reserved to hired for raid)
    async hireContractor(contractorCharacterId, hiredByCharacterId, raidId, tier = null) {
        if (!this.isOnline) {
            console.warn('Cannot hire contractor while offline');
            return { success: true }; // Allow local hiring when offline
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/characters?action=hire-contractor`, {
                method: 'POST',
                headers: this._authHeaders(hiredByCharacterId),
                body: JSON.stringify({
                    contractorCharacterId: contractorCharacterId,
                    hiredByCharacterId: hiredByCharacterId,
                    raidId: raidId,
                    tier: tier
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error hiring contractor:', error);
            return { success: false, message: 'Network error' };
        }
    }

    // Release a hired contractor (when raid ends)
    async releaseHiredContractor(contractorCharacterId, releasedByCharacterId) {
        if (!this.isOnline) {
            console.warn('Cannot release hired contractor while offline');
            return { success: true }; // Allow local release when offline
        }

        try {
            const response = await fetch(`${this.baseUrl}/api/characters?action=release-hired-contractor`, {
                method: 'POST',
                headers: this._authHeaders(releasedByCharacterId),
                body: JSON.stringify({
                    contractorCharacterId: contractorCharacterId,
                    releasedByCharacterId: releasedByCharacterId
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error releasing hired contractor:', error);
            return { success: false, message: 'Network error' };
        }
    }

    // Debug utility to clear all multiplayer data
    clearMultiplayerData() {
        debug(CH.ui, 'Clearing all multiplayer data...');
        localStorage.removeItem('multiplayerCharacters');
        this.localCharacters = [];
        debug(CH.ui, 'Multiplayer data cleared. Page refresh recommended.')
    }
}

// Initialize multiplayer system
export const multiplayer = new MultiplayerSystem();

// Utility functions for UI integration
export async function checkCharacterNameUnique(name) {
    // Check against local characters first
    const localDuplicate = gameState.characters.find(c =>
        c.name.toLowerCase() === name.toLowerCase()
    );
    if (localDuplicate) {
        return false;
    }

    // Then check multiplayer database
    return await multiplayer.isCharacterNameUnique(name);
}

// Debug utility to check all storage locations
export function debugCharacterStorage(characterName) {
    console.log('🔍 DEBUG: Checking all storage locations for character:', characterName);

    // Check main game state
    console.log('📋 Main gameState.character:', gameState.character);

    // Check multiplayer local characters
    console.log('🌐 Multiplayer localCharacters:', multiplayer.localCharacters);

    // Check all localStorage keys
    console.log('💾 All localStorage keys:');
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);
        console.log(`  ${key}:`, value);

        // Check if this value contains the character name
        if (value && value.includes(characterName)) {
            console.log(`  ⚠️ CHARACTER NAME FOUND IN: ${key}`);
        }
    }

    // Test name uniqueness check
    console.log('🔎 Testing name uniqueness check...');
    checkCharacterNameUnique(characterName).then(isUnique => {
        console.log(`  Name "${characterName}" is unique:`, isUnique);
    }).catch(error => {
        console.error('  Error checking name uniqueness:', error);
    });
}

// Clean up multiplayer storage on page load - only keep relevant characters
export function cleanupMultiplayerStorage() {
    debug(CH.ui, '🧹 Cleaning up multiplayer storage...');

    const relevantCharacterIds = new Set();

    // Keep current player character
    if (typeof gameState !== 'undefined' && gameState.character && gameState.character.multiplayerId) {
        relevantCharacterIds.add(gameState.character.multiplayerId);
        debug(CH.ui, `📝 Keeping current player character: ${gameState.character.name} (${gameState.character.multiplayerId})`);
    }

    // Keep characters in current raid team
    if (typeof gameState !== 'undefined' && gameState.currentRaid.hiredContractors) {
        gameState.currentRaid.hiredContractors.forEach(contractor => {
            if (contractor.multiplayerId) {
                relevantCharacterIds.add(contractor.multiplayerId);
                debug(CH.ui, `⚔️ Keeping hired contractor: ${contractor.name} (${contractor.multiplayerId})`);
            }
        });
    }

    // Keep characters in reserved contractors (for active raid)
    if (typeof gameState !== 'undefined' && gameState.currentRaid.reservedContractors) {
        gameState.currentRaid.reservedContractors.forEach(contractor => {
            if (contractor.multiplayerId) {
                relevantCharacterIds.add(contractor.multiplayerId);
                debug(CH.ui, `🔒 Keeping reserved contractor: ${contractor.name} (${contractor.multiplayerId})`);
            }
        });
    }

    debug(CH.ui, `🎯 Total relevant character IDs: ${relevantCharacterIds.size}`);

    // Filter multiplayer storage to only keep relevant characters
    const beforeCount = multiplayer.localCharacters.length;
    multiplayer.localCharacters = multiplayer.localCharacters.filter(char => {
        const isRelevant = relevantCharacterIds.has(char.id);
        if (!isRelevant) {
            debug(CH.ui, `🗑️ Removing orphaned character: ${char.name} (${char.id})`);
        }
        return isRelevant;
    });

    const afterCount = multiplayer.localCharacters.length;
    const removedCount = beforeCount - afterCount;

    if (removedCount > 0) {
        multiplayer.saveLocalCharacters();
        debug(CH.ui, `✅ Cleanup complete: removed ${removedCount} orphaned characters, kept ${afterCount}`);
    } else {
        debug(CH.ui, `✅ Cleanup complete: no orphaned characters found`);
    }
};

export async function validateCharacterName(name, currentName = null) {
    if (!name) {
        return { valid: false, message: '', className: 'hidden' };
    }

    if (name.length < 2) {
        return { valid: false, message: 'Name must be at least 2 characters', className: 'text-game-warning' };
    }

    if (name.length > 20) {
        return { valid: false, message: 'Name must be 20 characters or less', className: 'text-game-warning' };
    }

    // Skip uniqueness check if name hasn't changed
    if (currentName && name === currentName) {
        return { valid: true, message: '', className: 'hidden' };
    }

    try {
        const isUnique = await checkCharacterNameUnique(name);
        if (isUnique) {
            return { valid: true, message: '✅ Name is available', className: 'text-game-success' };
        } else {
            return { valid: false, message: '❌ Name already exists', className: 'text-game-danger' };
        }
    } catch (error) {
        return { valid: false, message: '⚠️ Could not check name availability', className: 'text-game-warning' };
    }
}

// Record contractor payments in database for multiplayer
export async function recordContractorPayments(contractorPayments) {
    // The hiring (active) character authenticates the payment: the server
    // verifies this character owns itself and actually hired each recipient.
    const hirer = gameState.character;
    for (const payment of contractorPayments) {
        if (payment.isPlayerContractor && payment.contractorId > 0) {
            try {
                // Create payment record in database - contractorId is the recipient character ID
                await multiplayer.createPaymentRecord({
                    recipientCharacterId: payment.contractorId,
                    hirerCharacterId: hirer?.multiplayerId || null,
                    hirerToken: hirer?.ownerToken || null,
                    amount: payment.credits,
                    xp: payment.xp || 0,
                    source: 'contractor_payment',
                    raidId: gameState.currentRaid?.id || null,
                    timestamp: new Date().toISOString()
                });
                debug(CH.ui, `Recorded payment of ${payment.credits} credits and ${payment.xp} XP for contractor ${payment.contractorName} (ID: ${payment.contractorId})`);
            } catch (error) {
                console.error(`Failed to record payment for contractor ${payment.contractorName}:`, error);
            }
        }
    }
}